// src/admin/pages/ProposalsAdmin.tsx
//
// Dashboard admin dedicata alle proposte di date libere dei clienti
// (booking con flag proposal=true). Paginato server-side, con tabs per
// stato e ricerca testuale su nome / email / titolo tour.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { Booking, BookingStatus } from '../../api/types';

type StatusFilter = BookingStatus | 'ALL';

const TABS: { key: StatusFilter; label: string }[] = [
  { key: 'PENDING', label: 'Da valutare' },
  { key: 'CONFIRMED', label: 'Accettate' },
  { key: 'CANCELLED', label: 'Annullate' },
  { key: 'ALL', label: 'Tutte' },
];

const PAGE_SIZES = [20, 50, 100];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
    CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    CANCELLED: 'bg-neutral-200 text-neutral-600 border-neutral-300',
    WAITLISTED: 'bg-sky-100 text-sky-800 border-sky-200',
  };
  const cls = colors[status] || 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${cls}`}>{status}</span>;
};

const fmt = (s?: string) => (s ? new Date(s).toLocaleDateString('it-IT') : '—');
const fmtDt = (s?: string) => (s ? new Date(s).toLocaleString('it-IT') : '—');

// Debounce hook: ritarda l'aggiornamento della search fino a quando l'utente
// smette di digitare per `delay` ms. Evita una chiamata BE ad ogni carattere.
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const ProposalsAdmin: React.FC = () => {
  const [items, setItems] = useState<Booking[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [tab, setTab] = useState<StatusFilter>('PENDING');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const params = useMemo(() => ({
    page,
    size,
    status: tab === 'ALL' ? undefined : (tab as BookingStatus),
    search: debouncedSearch.trim() || undefined,
  }), [page, size, tab, debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.proposals.page(params);
      setItems(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch {
      setBanner({ kind: 'err', text: 'Impossibile caricare le proposte.' });
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Ricarica al cambio di params.
  useEffect(() => { load(); }, [load]);

  // Ogni cambio di tab / search resetta la pagina a 0 (evita di finire su
  // pagina 3 quando il nuovo filtro ha solo 5 risultati).
  useEffect(() => { setPage(0); }, [tab, debouncedSearch, size]);

  const accept = async (b: Booking) => {
    if (!window.confirm(`Accettare la proposta di ${b.customerName} per il periodo ${fmt(b.checkInDate)} → ${fmt(b.checkOutDate)}?`)) return;
    try {
      await adminApi.bookings.confirm(b.id);
      setBanner({ kind: 'ok', text: `Proposta accettata: email di conferma inviata a ${b.customerEmail}.` });
      load();
    } catch (e: any) {
      setBanner({ kind: 'err', text: 'Errore: ' + (e?.response?.data?.message || e.message) });
    }
  };

  const reject = async (b: Booking) => {
    if (!window.confirm(`Rifiutare / annullare la proposta di ${b.customerName}? Il cliente riceverà una mail di annullamento.`)) return;
    try {
      await adminApi.bookings.cancel(b.id);
      setBanner({ kind: 'ok', text: 'Proposta annullata.' });
      load();
    } catch (e: any) {
      setBanner({ kind: 'err', text: 'Errore: ' + (e?.response?.data?.message || e.message) });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Proposte date libere</h1>
          <p className="text-neutral-600 mt-1 text-sm">
            Prenotazioni in cui il cliente ha suggerito lui stesso le date, senza slot fisso. Da valutare manualmente.
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 border border-neutral-300 rounded text-sm hover:bg-neutral-50">
          Aggiorna
        </button>
      </div>

      {banner && (
        <div className={`p-3 rounded text-sm border ${banner.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          {banner.text}
        </div>
      )}

      {/* Tabs stato */}
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === t.key ? 'border-neutral-900 text-neutral-900 font-semibold' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>
            {t.label}
            {tab === t.key && ` (${total})`}
          </button>
        ))}
      </div>

      {/* Search + page size */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome cliente, email, tour…"
          className="flex-1 min-w-[240px] px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
        />
        <label className="text-sm text-neutral-600 inline-flex items-center gap-2">
          Per pagina
          <select value={size} onChange={(e) => setSize(Number(e.target.value))}
                  className="px-2 py-1 border border-neutral-300 rounded text-sm">
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center text-neutral-500">
          <p>Nessuna proposta{search ? ' che corrisponda alla ricerca' : ''} in questa vista.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <div key={b.id} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="font-semibold text-neutral-900">{b.customerName}</h3>
                    <span className="inline-block bg-yellow-100 text-yellow-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-yellow-200">
                      Proposta
                    </span>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="text-sm text-neutral-600">
                    <a href={`mailto:${b.customerEmail}`} target="_blank" rel="noopener noreferrer" className="text-neutral-800 hover:underline">{b.customerEmail}</a>
                    {b.customerPhone && <> · <a href={`tel:${b.customerPhone}`} className="text-neutral-800 hover:underline">{b.customerPhone}</a></>}
                  </p>
                  <div className="mt-3 text-sm text-neutral-700 space-y-1">
                    <p><b>Tour:</b> {b.tour?.title || (b.slot as any)?.tour?.title || '—'}</p>
                    <p><b>Date proposte:</b> {fmt(b.checkInDate)} → {fmt(b.checkOutDate)}</p>
                    <p><b>Partecipanti:</b> {b.people}</p>
                    {b.notes && <p className="text-neutral-600 italic border-l-2 border-neutral-300 pl-3 mt-2">« {b.notes} »</p>}
                    <p className="text-xs text-neutral-400 mt-2">Ricevuta il {fmtDt(b.createdAt)}</p>
                  </div>
                </div>
                {b.status === 'PENDING' && (
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <button onClick={() => accept(b)} className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 font-medium">
                      Accetta proposta
                    </button>
                    <button type="button"
                            onClick={() => {
                              const subject = encodeURIComponent('Insolita Sardegna — la tua proposta');
                              window.location.href = `mailto:${b.customerEmail}?subject=${subject}`;
                            }}
                            className="px-4 py-2 border border-neutral-300 rounded text-sm text-center hover:bg-neutral-50">
                      Rispondi via email
                    </button>
                    <button onClick={() => reject(b)} className="px-4 py-2 text-red-600 border border-red-200 rounded text-sm hover:bg-red-50">
                      Annulla
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginator */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <p className="text-neutral-600">
            Mostrando {items.length === 0 ? 0 : (page * size + 1)}–{page * size + items.length} di {total}
          </p>
          <div className="flex items-center gap-2">
            <button disabled={page === 0} onClick={() => setPage(0)}
                    className="px-3 py-1.5 border border-neutral-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50">
              ⏮
            </button>
            <button disabled={page === 0} onClick={() => setPage(page - 1)}
                    className="px-3 py-1.5 border border-neutral-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50">
              ← Prec
            </button>
            <span className="px-3 py-1.5 text-neutral-700">Pagina {page + 1} di {totalPages}</span>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}
                    className="px-3 py-1.5 border border-neutral-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50">
              Succ →
            </button>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(totalPages - 1)}
                    className="px-3 py-1.5 border border-neutral-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50">
              ⏭
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalsAdmin;
